import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlaceTitle } from '../nav/NavSteps';
import { useUrlState } from '../nav/urlState';
import { parseCatalogEdit } from '../nav/catalogLink';
import { isLocalPath } from '../nav/localPath';
import { dropDraft } from '../nav/drafts';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { CustomFieldsSettings } from './CustomFieldsSettings';
import { countText, groupItems, orderCategoryOf, withItemOrder } from './grouping';
import { isPacklistPick, pickTarget, returnUrl } from './pick';
import { ItemDetails } from './ItemDetails';
import { seesSupplierOwners } from './SupplierOwners';
import { ItemInfo } from './ItemInfo';
import { TodoDot } from './TodoDot';
import { CATALOG_KEY, CATEGORIES_KEY, categoryLabels, ItemForm, useCategories, useLocations, type Category, type Item } from './ItemForm';
import { LocationsSettings } from './LocationsSettings';
import { DuplicatesPanel } from './DuplicatesPanel';
import { CatalogExcel } from './CatalogExcel';
import type { SimilarItem } from './DuplicateNameHint';
import { moved } from './CategoryOrderPage';
import { formatPln } from './money';
import { whereShort } from './whereNow';
import './catalog.css';
import { leaveGuarded, useUnsavedGuard, validForm } from '../ui/unsaved';
import { DialogLayer } from '../ui/Dialog';

const RESOURCE = 'catalog.items';
const SUPPLIER_PRICES = 'subrentals.prices';
type ListResponse = { items: Item[]; truncated: boolean; incompleteCount?: number };
type CategoryTotal = { categoryId: string | null; items: number; pieces: number };

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

function DeleteDialog({ item, onClose }: { item: Item; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/catalog/items/${item.id}?version=${item.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="delete-title">
        <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">Usunąć pozycję?</h2>
        <p>„{item.name}” zniknie z katalogu. Pozycja zostaje w bazie, a w historii zmian zostaje wpis o usunięciu.</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń pozycję'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

/** The category and every category below it (to keep them out of its "Kategoria nadrzędna" choices). */
function subtreeOf(categories: Category[], id: string): Set<string> {
  const out = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const c of categories) if (c.parentId !== null && out.has(c.parentId) && !out.has(c.id)) grew = out.add(c.id).size > 0;
  }
  return out;
}

/** One category of the tree: name and parent edited in place, ↑ / ↓ among its siblings, "+ Podkategoria". */
function CategoryRow({
  category,
  label,
  depth,
  categories,
  siblings,
  canEdit,
  canCreate,
  onAddSub,
}: {
  category: Category;
  label: string;
  depth: number;
  categories: Category[];
  siblings: string[];
  canEdit: boolean;
  canCreate: boolean;
  onAddSub: (parentId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(category.name);
  const [parentId, setParentId] = useState(category.parentId ?? '');
  const save = useMutation({
    mutationFn: () =>
      api(`/api/catalog/categories/${category.id}`, {
        method: 'PATCH',
        body: { version: category.version, ...(name.trim() !== category.name ? { name: name.trim() } : {}), ...(parentId !== (category.parentId ?? '') ? { parentId: parentId || null } : {}) },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY }),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api('/api/catalog/categories/order', { method: 'PUT', body: { parentId: category.parentId, ids } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY }),
  });
  // P8 (points 10, 11): saved at once, like a switch
  const auto = useMutation({
    mutationFn: (autoQuoteSection: boolean) => api(`/api/catalog/categories/${category.id}`, { method: 'PATCH', body: { version: category.version, autoQuoteSection } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CATALOG_KEY }),
  });
  const index = siblings.indexOf(category.id);
  const below = subtreeOf(categories, category.id);
  const parents = categoryLabels(categories).filter((c) => !below.has(c.id));
  const changed = name.trim() !== category.name || parentId !== (category.parentId ?? '');
  const error = [save.error, reorder.error, auto.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  // owner 2026-10-23 21:14: a renamed or moved category not saved: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: canEdit && changed,
    save: async (el) => {
      if (!validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <li className="row cat-tree-row" style={{ paddingLeft: `${depth * 1.5}rem` }} data-depth={depth}>
      <form
        ref={guardRef}
        className="row-main cat-tree-form"
        aria-label={`Kategoria: ${label}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) save.mutate();
        }}
      >
        {error && (
          <p className="alert" role="alert">
            {error.status === 409 ? 'Ktoś już zmienił tę kategorię. Odśwież stronę.' : error.message}
          </p>
        )}
        <label className="field w-md">
          <span>Nazwa kategorii</span>
          <input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} required />
        </label>
        <label className="field w-md">
          <span>Kategoria nadrzędna</span>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} disabled={!canEdit}>
            <option value="">Brak (główna)</option>
            {parents.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        {canEdit && changed && (
          <button className="btn small primary" type="submit" disabled={save.isPending}>
            Zapisz
          </button>
        )}
        <label className="check cat-auto-quote" data-feature-id="catalog.auto_quote_section">
          <input type="checkbox" checked={!!category.autoQuoteSection} disabled={!canEdit || auto.isPending} onChange={(e) => auto.mutate(e.target.checked)} />
          <span>Dodawaj automatycznie do nowego kosztorysu</span>
        </label>
        <span className="actions cat-tree-actions">
          {canEdit && (
            <>
              <button className="btn small" type="button" aria-label={`W górę: ${label}`} disabled={index <= 0 || reorder.isPending} onClick={() => reorder.mutate(moved(siblings, index, index - 1))}>
                ↑
              </button>
              <button className="btn small" type="button" aria-label={`W dół: ${label}`} disabled={index >= siblings.length - 1 || reorder.isPending} onClick={() => reorder.mutate(moved(siblings, index, index + 1))}>
                ↓
              </button>
            </>
          )}
          {canCreate && (
            <button className="btn small ghost" type="button" aria-label={`Dodaj podkategorię: ${label}`} onClick={() => onAddSub(category.id)}>
              + Podkategoria
            </button>
          )}
        </span>
      </form>
    </li>
  );
}

/**
 * "Kategorie" behind the catalog's gear (remark 4/31): the tree (main categories and their subcategories), renaming,
 * moving under another parent, ↑ / ↓ among siblings, adding a category or a subcategory.
 */
function CategoriesPanel({ canCreate, canEdit }: { canCreate: boolean; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const categories = useCategories(true).data?.categories ?? [];
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const add = useMutation({
    mutationFn: () => api('/api/catalog/categories', { method: 'POST', body: { name: name.trim(), ...(parentId ? { parentId } : {}) } }),
    onSuccess: async () => {
      setName('');
      await queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate();
  }
  const addRef = useUnsavedGuard({
    dirty: canCreate && name.trim() !== '',
    save: async () => {
      await add.mutateAsync();
      return true;
    },
  });
  const byId = new Map(categories.map((c) => [c.id, c]));
  const live = (id: string | null) => id !== null && byId.has(id);
  const siblingsOf = (parent: string | null) => categories.filter((c) => (live(c.parentId) ? c.parentId : null) === parent).map((c) => c.id);
  const depthOf = (c: Category) => {
    let depth = 0;
    const seen = new Set<string>();
    for (let at = c.parentId; at && byId.has(at) && !seen.has(at); at = byId.get(at)!.parentId) {
      seen.add(at);
      depth++;
    }
    return depth;
  };
  return (
    <section className="panel settings-panel" aria-label="Kategorie sprzętu" data-feature-id="catalog.categories">
      <h2>Kategorie i podkategorie</h2>
      <p className="muted">
        Podkategorię dodasz przyciskiem „+ Podkategoria” albo wybierając „Kategorię nadrzędną”. Strzałki ↑ ↓ zmieniają kolejność wśród kategorii tego samego poziomu (zapis od razu); całe drzewo naraz ułożysz w{' '}
        <Link to="/katalog/kolejnosc-kategorii" data-feature-id="catalog.category_order">
          Kolejność kategorii
        </Link>
        .
      </p>
      {categories.length === 0 && <p className="muted">Nie ma jeszcze kategorii.</p>}
      <ul className="list cat-tree">
        {categoryLabels(categories).map(({ id, label }) => {
          const c = byId.get(id)!;
          return (
            <CategoryRow
              key={`${c.id}-${c.version}`}
              category={c}
              label={label}
              depth={depthOf(c)}
              categories={categories}
              siblings={siblingsOf(live(c.parentId) ? c.parentId : null)}
              canEdit={canEdit}
              canCreate={canCreate}
              onAddSub={(parent) => {
                setParentId(parent);
                nameRef.current?.focus();
              }}
            />
          );
        })}
      </ul>
      {canCreate && (
        <form ref={addRef} className="edit inline-form" aria-label="Nowa kategoria" onSubmit={submit}>
          {add.error instanceof ApiError && (
            <p className="alert" role="alert">
              {add.error.message}
            </p>
          )}
          <label className="field w-md">
            <span>Nazwa nowej kategorii</span>
            <input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
          </label>
          <label className="field w-md">
            <span>Kategoria nadrzędna</span>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">Brak (główna)</option>
              {categoryLabels(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button className="btn" type="submit" disabled={add.isPending}>
            {parentId ? 'Dodaj podkategorię' : 'Dodaj kategorię'}
          </button>
        </form>
      )}
    </section>
  );
}

export function CatalogPage() {
  const { me } = useMe();
  const navigate = useNavigate();
  // P13: the search, category, filter, open item and open form live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const search = params.get('szukaj') ?? '';
  const setSearch = (v: string) => update({ szukaj: v });
  const categoryId = params.get('kategoria') ?? '';
  const setCategoryId = (v: string) => update({ kategoria: v });
  /** "Do uzupełnienia": only items lacking a price, value, factor or location (owner 2026-10-19). */
  const incomplete = params.get('do-uzupelnienia') === '1';
  const setIncomplete = (v: boolean) => update({ 'do-uzupelnienia': v ? '1' : null });
  const creating = params.get('nowa') === '1';
  const setCreating = (v: boolean) => update({ nowa: v ? '1' : null });
  /** "Duplikuj" (owner 23:16): the item the new-item form is filled from (kept only while the screen is open). */
  const [copying, setCopying] = useState<Item | null>(null);
  const editingId = params.get('edycja');
  const setEditingId = (id: string | null) => update({ edycja: id });
  const detailsId = params.get('pozycja');
  const setDetailsId = (id: string | null) => update({ pozycja: id });
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Item | null>(null);
  const panelBox = useRef<HTMLDivElement>(null);
  const [panel, setPanel] = useState<'' | 'categories' | 'custom-fields' | 'locations' | 'duplicates'>('');
  const q = useDebounced(search.trim(), 250);
  const queryClient = useQueryClient();
  const [orderError, setOrderError] = useState('');
  const [dragging, setDragging] = useState<{ group: string; index: number } | null>(null);
  /** The item whose name takes the focus back after it moved (⌥↑ / ⌥↓). */
  const focusAfterMove = useRef<string | null>(null);
  /** Order changes go out one after another: each one's `before` is what the previous one left. */
  const orderChain = useRef<Promise<void>>(Promise.resolve());
  const canViewCategories = !!me && can(me, 'catalog.categories', 'view');
  // owner 2026-10-21 00:32: the quote's "Wybierz z katalogu" opens the catalog in a picking mode
  const pick = pickTarget(params);
  const categories = useCategories(canViewCategories).data?.categories ?? [];
  const locationNames = new Map((useLocations().data?.locations ?? []).map((l) => [l.id, l.name]));

  // items and pieces per category with its subcategories (remark 4/30)
  const totals = useQuery({
    queryKey: [...CATALOG_KEY, 'items', 'totals'],
    queryFn: () => api<{ totals: CategoryTotal[] }>('/api/catalog/categories/totals'),
  });
  const totalsById = new Map((totals.data?.totals ?? []).map((t) => [t.categoryId, t]));
  const totalOf = (id: string | null) => totalsById.get(id);
  const listKey = [...CATALOG_KEY, 'items', q, categoryId, incomplete];
  const list = useQuery({
    queryKey: listKey,
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (categoryId) params.set('categoryId', categoryId);
      if (incomplete) params.set('incomplete', '1');
      const qs = params.toString();
      return api<ListResponse>(`/api/catalog/items${qs ? `?${qs}` : ''}`);
    },
  });
  const itemIds = (list.data?.items ?? []).map((i) => i.id).join(',');
  // what is free is the quote's (the packlist's own picker shows it as it adds)
  const quotePick = pick && !isPacklistPick(pick) ? pick : null;
  const free = useQuery({
    queryKey: ['quotes', 'free', quotePick?.versionId, itemIds],
    enabled: quotePick !== null && itemIds !== '' && !!me && can(me, 'availability.reservations', 'view'),
    queryFn: () => api<{ free: Record<string, number> }>(`/api/quotes/versions/${quotePick!.versionId}/free?itemIds=${itemIds}`),
  });
  // Ctrl+klik on a line of a quote / packlist (owner 2026-10-23 20:53): `?edytuj=<itemId>&powrot=…` finds the item
  // (the search set to its name), opens its editor — or, without the right to edit, its details — scrolls to it and
  // marks it for a moment; a deleted item says so calmly. „Wstecz” goes back to the quote, closing the form stays here.
  const deepEdit = pick ? null : parseCatalogEdit(params);
  const deepId = deepEdit?.id ?? null;
  const backRaw = params.get('powrot');
  const back = !pick && backRaw && isLocalPath(backRaw) ? backRaw : null;
  const [missing, setMissing] = useState('');
  // owner 2026-10-24 10:53 („zapis i powrót do kosztorysu”): the editor opened from a line — its save goes back there
  const [fromLine, setFromLine] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const flashed = useRef<string | null>(null);
  const editTarget = useQuery({
    queryKey: [...CATALOG_KEY, 'item', deepId ?? ''],
    enabled: deepId !== null && !!me,
    retry: false,
    queryFn: () => api<{ id: string; name: string }>(`/api/catalog/items/${encodeURIComponent(deepId!)}`),
  });
  useEffect(() => {
    if (deepId === null || !me) return;
    if (editTarget.data && editTarget.data.id === deepId) {
      const mayEdit = can(me, RESOURCE, 'edit');
      setMissing('');
      setFlash(deepId);
      setFromLine(mayEdit ? deepId : null);
      flashed.current = null;
      update({ edytuj: null, szukaj: editTarget.data.name, kategoria: null, 'do-uzupelnienia': null, nowa: null, edycja: mayEdit ? deepId : null, pozycja: mayEdit ? null : deepId });
    } else if (editTarget.error) {
      setMissing(editTarget.error instanceof ApiError && editTarget.error.status !== 404 ? editTarget.error.message : 'Tej pozycji nie ma już w katalogu.');
      update({ edytuj: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepId, editTarget.data, editTarget.error, me]);
  useEffect(() => {
    if (!flash || flashed.current === flash) return;
    const el = document.querySelector(`[data-item-id="${CSS.escape(flash)}"]`);
    if (!el) return;
    flashed.current = flash;
    el.scrollIntoView?.({ block: 'center' });
    window.setTimeout(() => setFlash((f) => (f === flash ? null : f)), 2500);
  });
  useEffect(() => {
    const id = focusAfterMove.current;
    if (id === null) return;
    focusAfterMove.current = null;
    document.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(id)}"] .cat-open`)?.focus();
  });
  // P13: the place's name in the list of recent places ("Katalog — Mikrofony", "Katalog — Nowa pozycja")
  const named = (id: string | null) => (id ? list.data?.items.find((i) => i.id === id)?.name : undefined);
  const categoryName = categories.find((c) => c.id === categoryId)?.name;
  const placeName = creating ? 'Nowa pozycja' : (named(editingId) ?? named(detailsId) ?? categoryName ?? (incomplete ? 'Do uzupełnienia' : null));
  usePlaceTitle(placeName ? `Katalog — ${placeName}` : 'Katalog sprzętu');
  if (!me) return null;
  const items = list.data?.items ?? [];
  // which fields the user may see and set comes from the session (`hiddenFields`, rule 7a), not from the rows
  const visible = (field: string) => fieldVisible(me, RESOURCE, field);
  // the settings panel shown is replaced or hidden: its unsaved changes ask first (owner 2026-10-23 21:14)
  const togglePanel = (name: 'categories' | 'custom-fields' | 'locations' | 'duplicates') => leaveGuarded(() => setPanel(panel === name ? '' : name), panelBox.current);
  // while an item is being added or edited the page shows only "Zapisz zmiany" / "Odrzuć zmiany" (owner remark 3/2)
  const formOpen = creating || copying !== null || editingId !== null;
  const canDuplicate = can(me, RESOURCE, 'create');
  const duplicate = (it: Item) =>
    leaveGuarded(() => {
      setPanel('');
      setDetailsId(null);
      setCopying(it);
    }, panelBox.current);
  /**
   * P14: show an existing item instead (the name typed is already in the catalog, or "Otwórz" in "Znajdź duble"): the
   * form closes, the list finds the item by name; "Dodaj sztuki do istniejącej" opens its edit form (quantity items)
   * or its details with the units (items with units).
   */
  const showExisting = (it: Pick<SimilarItem, 'id' | 'name' | 'kind'>, how: 'open' | 'add' = 'open') => {
    setCreating(false);
    setCopying(null);
    setEditingId(null);
    setCategoryId('');
    setIncomplete(false);
    setSearch(it.name);
    if (how === 'add' && it.kind !== 'serialized' && can(me, RESOURCE, 'edit')) setEditingId(it.id);
    else setDetailsId(it.id);
  };
  const openForm = (id: string | null) =>
    leaveGuarded(() => {
      setPanel('');
      if (id === null) setCreating(true);
      else setEditingId(id);
    });
  // rarer settings behind the small gear (rule 9 of docs/wyglad.md); kits have no menu tab (owner remark 3/1)
  const settings = [
    ...(canViewCategories ? [{ label: panel === 'categories' ? 'Ukryj kategorie' : 'Kategorie', onSelect: () => togglePanel('categories') }] : []),
    ...(canViewCategories ? [{ label: 'Kolejność kategorii', onSelect: () => navigate('/katalog/kolejnosc-kategorii') }] : []),
    ...(can(me, 'catalog.settings', 'view') ? [{ label: panel === 'custom-fields' ? 'Ukryj pola własne' : 'Pola własne', onSelect: () => togglePanel('custom-fields') }] : []),
    ...(can(me, 'catalog.settings', 'view') ? [{ label: panel === 'locations' ? 'Ukryj lokalizacje' : 'Lokalizacje', onSelect: () => togglePanel('locations') }] : []),
    { label: panel === 'duplicates' ? 'Ukryj duble' : 'Znajdź duble', onSelect: () => togglePanel('duplicates') },
    ...(can(me, 'kits.kits', 'view') ? [{ label: 'Zestawy…', onSelect: () => navigate('/szablony/zestawy') }] : []),
  ];
  const showPrice = visible('rentalPriceDayGrosze');
  // B1 "Wybierz z katalogu": a column "Wolne w terminie" (the event's days) and a "Wybierz" button per item
  const showFree = pick !== null && free.data !== undefined;
  const columns = (showPrice ? 5 : 4) + (showFree ? 1 : 0);
  // catalog order (owner's remark 14): rows under a small header per category
  const groups = groupItems(items, categories);
  // manual order (remark 4/24): only on the whole category (no search, nothing cut off) and not while a form is open
  const canReorder = can(me, RESOURCE, 'edit') && visible('sort') && search.trim() === '' && q === '' && !incomplete && !list.data?.truncated && !formOpen;
  const incompleteCount = list.data?.incompleteCount ?? 0;
  /** Moves the item at `index` of `ids` to `to`, shows it at once and saves it (after any move still going out). */
  const moveItem = (categoryKey: string | null, ids: string[], index: number, to: number, focus = false) => {
    if (to < 0 || to >= ids.length || to === index) return;
    const next = moved(ids, index, to);
    setOrderError('');
    queryClient.setQueryData<ListResponse>(listKey, (old) => old && { ...old, items: withItemOrder(old.items, next) });
    if (focus) focusAfterMove.current = ids[index]!;
    orderChain.current = orderChain.current.then(async () => {
      try {
        await api('/api/catalog/items/order', { method: 'PUT', body: { categoryId: categoryKey, ids: next, before: ids } });
      } catch (e) {
        setOrderError(
          e instanceof ApiError && e.status === 409
            ? 'Ktoś w międzyczasie zmienił tę kategorię. Pokazuję aktualną kolejność, przesuń pozycję jeszcze raz.'
            : e instanceof ApiError
              ? e.message
              : 'Nie udało się zapisać kolejności.',
        );
        await queryClient.invalidateQueries({ queryKey: [...CATALOG_KEY, 'items'] });
      }
    });
  };

  return (
    <section className="page catalog-page" data-feature-id="catalog.list">
      <div className="page-head">
        <h1>Katalog sprzętu</h1>
        {!formOpen && !pick && (
          <div className="actions row-actions">
            {can(me, SUPPLIER_PRICES, 'view') && (
              <Link className="btn" to="/katalog/dostawcy" data-feature-id="subrentals.supplier_catalog">
                Katalogi dostawców
              </Link>
            )}
            {can(me, RESOURCE, 'create') && (
              <button className="btn primary" type="button" data-feature-id="catalog.edit" onClick={() => openForm(null)}>
                Dodaj pozycję
              </button>
            )}
            <RowMenu icon="gear" label="Ustawienia katalogu" featureId="catalog.settings" actions={settings} />
          </div>
        )}
      </div>

      {/* „Eksportuj do Excela” / „Wczytaj z Excela” (owner 2026-11-04): the owner only, like the data export */}
      {me.isOwner && !formOpen && !pick && <CatalogExcel />}
      {back && (
        <div className="pick-bar" data-feature-id="catalog.open_from_line">
          <Link className="btn small" to={back}>
            ← Wróć
          </Link>
        </div>
      )}
      {missing && (
        <p className="notice" role="status">
          {missing}
        </p>
      )}
      {pick && (
        <div className="pick-bar">
          <p className="notice pick-notice" role="status">
            {isPacklistPick(pick)
              ? 'Wybierasz sprzęt do sprzętówki: kliknij „Wybierz” przy pozycji — wrócisz do sprzętówki z dodaną pozycją.'
              : 'Wybierasz sprzęt do kosztorysu: kliknij „Wybierz” przy pozycji — wrócisz do kosztorysu z dodaną pozycją.'}
          </p>
          <Link className="btn small" to={pick.back}>
            {isPacklistPick(pick) ? 'Wróć do sprzętówki' : 'Wróć do kosztorysu'}
          </Link>
        </div>
      )}
      {(creating || copying) && (
        <ItemForm
          key={copying?.id ?? 'new'}
          {...(copying ? { copyFrom: copying } : {})}
          visible={visible}
          canPickCategory={canViewCategories}
          canSeeContractors={can(me, 'contractors.contractors', 'view')}
          onDone={() => {
            if (!copying) dropDraft('catalog.item:new');
            setCreating(false);
            setCopying(null);
          }}
          onExisting={showExisting}
        />
      )}
      <div ref={panelBox} style={{ display: 'contents' }}>
      {panel === 'categories' && <CategoriesPanel canCreate={can(me, 'catalog.categories', 'create')} canEdit={can(me, 'catalog.categories', 'edit')} />}
      {panel === 'custom-fields' && <CustomFieldsSettings />}
      {panel === 'duplicates' && <DuplicatesPanel canMerge={can(me, RESOURCE, 'edit') && can(me, RESOURCE, 'delete')} onOpen={(it) => showExisting(it)} />}
      {panel === 'locations' && <LocationsSettings canCreate={can(me, 'catalog.settings', 'create')} canEdit={can(me, 'catalog.settings', 'edit')} canDelete={can(me, 'catalog.settings', 'delete')} />}
      </div>

      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nazwa, producent, model" autoComplete="off" />
        </label>
        {canViewCategories && (
          <label className="field">
            <span>Kategoria</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Wszystkie</option>
              {categoryLabels(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {(incompleteCount > 0 || incomplete) && (
          <label className="check todo-filter" data-feature-id="catalog.todo">
            <input type="checkbox" checked={incomplete} onChange={(e) => setIncomplete(e.target.checked)} />
            <span className="todo-dot" aria-hidden="true" />
            <span>
              Do uzupełnienia <span className="muted">({incompleteCount})</span>
            </span>
          </label>
        )}
      </div>

      {categoryId && totalOf(categoryId) && !incomplete && (
        <p className="cat-summary" data-testid="category-summary">
          <strong>{categories.find((c) => c.id === categoryId)?.name}:</strong> {countText(totalOf(categoryId)!)}
          <span className="muted"> (z podkategoriami)</span>
        </p>
      )}
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać katalogu'}
        </p>
      )}
      {list.data && items.length === 0 && <p className="muted">Nie znaleziono pozycji.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 pozycji. Zawęź wyszukiwanie, żeby zobaczyć pozostałe.</p>}
      {orderError && (
        <p className="alert" role="alert">
          {orderError}
        </p>
      )}

      {items.length > 0 && (
        <table className="table cat-table" aria-label="Pozycje katalogu">
          <thead>
            <tr>
              <th scope="col">Nazwa</th>
              {showPrice && (
                <th scope="col" className="num">
                  Cena za dobę
                </th>
              )}
              <th scope="col" className="num">
                Ilość
              </th>
              <th scope="col" className="num">
                Dostępne teraz
              </th>
              {showFree && (
                <th scope="col" className="num" title="Ile sztuk jest wolnych w dniach wydarzenia kosztorysu">
                  Wolne w terminie
                </th>
              )}
              <th scope="col" className="cat-actions-col">
                <span className="visually-hidden">Działania</span>
              </th>
            </tr>
          </thead>
          {groups.map((group) => {
            const groupKey = group.id ?? 'none';
            const orderCategory = canReorder ? orderCategoryOf(group) : undefined;
            const ordered = orderCategory !== undefined && group.items.length > 1;
            const groupIds = group.items.map((i) => i.id);
            const move = (index: number, to: number, focus?: boolean) => moveItem(orderCategory ?? null, groupIds, index, to, focus);
            return (
            <Fragment key={groupKey}>
              {group.label && (
                <tbody className={`cat-group${group.depth > 0 ? ' sub' : ''}`} data-depth={group.depth}>
                  <tr>
                    <th scope="rowgroup" colSpan={columns} aria-label={group.label}>
                      <span className="cat-group-name">{group.name}</span>
                      {totalOf(group.id) && (
                        <span className="cat-group-count" title="Razem z podkategoriami">
                          {countText(totalOf(group.id)!)}
                        </span>
                      )}
                    </th>
                  </tr>
                </tbody>
              )}
              {group.items.map((it, index) => {
                const editing = editingId === it.id;
                const details = detailsId === it.id;
                const history = (editing || historyId === it.id) && can(me, 'history.entries', 'view');
                const maker = [it.manufacturer, it.model].filter(Boolean).join(' ');
                const where = whereShort(it.placements ?? [], locationNames);
                const more = [
                  ...(ordered && index > 0 ? [{ label: 'Przesuń wyżej', onSelect: () => move(index, index - 1, true) }] : []),
                  ...(ordered && index < group.items.length - 1 ? [{ label: 'Przesuń niżej', onSelect: () => move(index, index + 1, true) }] : []),
                  ...(canDuplicate ? [{ label: 'Duplikuj', onSelect: () => duplicate(it) }] : []),
                  ...(can(me, 'history.entries', 'view') ? [{ label: historyId === it.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === it.id ? null : it.id) }] : []),
                  ...(can(me, RESOURCE, 'delete') ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(it) }] : []),
                ];
                const onNameKey = (e: KeyboardEvent<HTMLButtonElement>) => {
                  if (!ordered || !e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                  e.preventDefault();
                  move(index, e.key === 'ArrowUp' ? index - 1 : index + 1, true);
                };
                return (
                  <tbody
                    key={it.id}
                    className={`cat-item${details || editing ? ' open' : ''}${flash === it.id ? ' cat-flash' : ''}${dragging?.group === groupKey && dragging.index === index ? ' dragging' : ''}`}
                    aria-label={it.name}
                    data-item-id={it.id}
                    onDragOver={(e) => {
                      if (dragging?.group === groupKey) e.preventDefault();
                    }}
                    onDrop={(e) => {
                      if (dragging?.group !== groupKey) return;
                      e.preventDefault();
                      move(dragging.index, index);
                      setDragging(null);
                    }}
                  >
                    <tr className="cat-row">
                      <td className="cat-name">
                        {ordered && (
                          <span
                            className="cat-handle"
                            data-feature-id="catalog.item_order"
                            draggable
                            aria-hidden="true"
                            title="Przeciągnij, żeby zmienić kolejność"
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', it.id);
                              setDragging({ group: groupKey, index });
                            }}
                            onDragEnd={() => setDragging(null)}
                          />
                        )}
                        <ItemInfo item={it} visible={visible} names={locationNames}>
                          {(describedBy) => (
                            <button
                              type="button"
                              className="cat-open"
                              aria-expanded={details}
                              aria-label={`Szczegóły: ${it.name}`}
                              aria-describedby={describedBy}
                              aria-keyshortcuts={ordered ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
                              onClick={() => setDetailsId(details ? null : it.id)}
                              onKeyDown={onNameKey}
                            >
                              {it.name}
                            </button>
                          )}
                        </ItemInfo>
                        <TodoDot missing={it.missingFields} />
                        {maker && maker.toLocaleLowerCase('pl') !== it.name.toLocaleLowerCase('pl') && <span className="cat-maker">{maker}</span>}
                        {where && (
                          <span className="cat-where" title="Gdzie teraz (reszta w domyślnej lokalizacji)">
                            {where}
                          </span>
                        )}
                        <span className="cat-phone-qty">
                          {it.ownedQuantity} / {it.totalQuantity} dostępne
                        </span>
                      </td>
                      {showPrice && <td className="num cat-price">{it.rentalPriceDayGrosze != null ? formatPln(it.rentalPriceDayGrosze) : ''}</td>}
                      <td className="num cat-qty" data-col="total">
                        {it.totalQuantity}
                      </td>
                      <td className="num cat-qty" data-col="available">
                        {it.ownedQuantity}
                      </td>
                      {showFree && (
                        <td className={`num cat-qty${(free.data?.free[it.id] ?? 0) <= 0 ? ' cat-none-free' : ''}`} data-col="free">
                          {free.data?.free[it.id] ?? ''}
                        </td>
                      )}
                      <td className="cat-actions">
                        {pick && (
                          <button className="btn small primary" type="button" aria-label={`Wybierz: ${it.name}`} onClick={() => navigate(returnUrl(pick, { kind: 'item', id: it.id }))}>
                            Wybierz
                          </button>
                        )}
                        {!formOpen && !pick && (
                          <>
                            {can(me, RESOURCE, 'edit') && (
                              <button className="btn small" type="button" data-feature-id="catalog.edit" aria-label={`Edytuj: ${it.name}`} onClick={() => openForm(it.id)}>
                                Edytuj
                              </button>
                            )}
                            <RowMenu label={`Więcej działań: ${it.name}`} actions={more} />
                          </>
                        )}
                      </td>
                    </tr>
                    {(editing || details || history) && (
                      <tr className="cat-expand">
                        <td colSpan={columns}>
                          {editing && (
                            <ItemForm
                              item={it}
                              visible={visible}
                              canPickCategory={canViewCategories}
                              canSeeContractors={can(me, 'contractors.contractors', 'view')}
                              canSeeSupplierOwners={seesSupplierOwners(me)}
                              onDone={() => {
                                dropDraft(`catalog.item:${it.id}`);
                                setEditingId(null);
                              }}
                              onSaved={back && fromLine === it.id ? () => navigate(back) : undefined}
                              onExisting={showExisting}
                            />
                          )}
                          {details && !editing && <ItemDetails itemId={it.id} />}
                          {history && <HistoryPanel entityId={it.id} resource={RESOURCE} />}
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </Fragment>
            );
          })}
        </table>
      )}

      {deleting && <DeleteDialog item={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}
